import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import AdminLayout from '../components/AdminLayout';
import { smartRatingsAPI } from '../api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Trash2, Edit, Plus, Upload } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const AdminSmartRatings = () => {
  const [smartRatings, setSmartRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    label: '',
    icon: '',
    order: 0,
    is_active: true,
  });

  useEffect(() => {
    loadSmartRatings();
  }, []);

  const loadSmartRatings = async () => {
    try {
      const response = await smartRatingsAPI.getAll();
      setSmartRatings(response.data);
    } catch (error) {
      console.error('Error loading smart ratings:', error);
      toast({
        title: 'Hata',
        description: 'Akıllı işaretler yüklenirken bir hata oluştu.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await smartRatingsAPI.update(editingId, formData);
        toast({
          title: 'Başarılı',
          description: 'Akıllı işaret güncellendi.',
        });
      } else {
        await smartRatingsAPI.create(formData);
        toast({
          title: 'Başarılı',
          description: 'Akıllı işaret eklendi.',
        });
      }
      resetForm();
      loadSmartRatings();
    } catch (error) {
      console.error('Error saving smart rating:', error);
      toast({
        title: 'Hata',
        description: 'Akıllı işaret kaydedilirken bir hata oluştu.',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (rating) => {
    setFormData({
      label: rating.label,
      icon: rating.icon,
      order: rating.order,
      is_active: rating.is_active,
    });
    setEditingId(rating._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu akıllı işareti silmek istediğinizden emin misiniz?')) return;
    
    try {
      await smartRatingsAPI.delete(id);
      toast({
        title: 'Başarılı',
        description: 'Akıllı işaret silindi.',
      });
      loadSmartRatings();
    } catch (error) {
      console.error('Error deleting smart rating:', error);
      toast({
        title: 'Hata',
        description: 'Akıllı işaret silinirken bir hata oluştu.',
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setFormData({
      label: '',
      icon: '',
      order: 0,
      is_active: true,
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // For now, we'll use a placeholder image URL
    // In production, you would upload to a storage service
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData({ ...formData, icon: reader.result });
    };
    reader.readAsDataURL(file);
  };

  return (
    <AdminLayout>
      <Helmet>
        <title>Akıllı İşaretler Yönetimi - Cinefora Admin</title>
      </Helmet>
      
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-3xl font-bold text-white">Film Derecelendirme ve Akıllı İşaretler</h1>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-[#FDB714] hover:bg-[#e5a510] text-black"
        >
          <Plus className="mr-2" size={20} />
          Yeni Ekle
        </Button>
      </div>

      {showForm && (
        <Card className="bg-gray-900 border-gray-800 p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">
            {editingId ? 'Akıllı İşaret Düzenle' : 'Yeni Akıllı İşaret Ekle'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-gray-300 block mb-2">Etiket</label>
              <input
                type="text"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                placeholder="Örn: Genel İzleyici Kitlesi İçin Uygundur"
                required
              />
            </div>

            <div>
              <label className="text-gray-300 block mb-2">İkon/Resim</label>
              <div className="flex gap-4 items-start">
                <div className="flex-1">
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white mb-2"
                    placeholder="Resim URL'si"
                  />
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg cursor-pointer">
                      <Upload size={18} />
                      <span>Resim Yükle</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
                {formData.icon && (
                  <div className="w-24 h-24 border border-gray-700 rounded-lg overflow-hidden">
                    <img src={formData.icon} alt="Preview" className="w-full h-full object-contain bg-gray-800" />
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="text-gray-300 block mb-2">Sıralama</label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                min="0"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="is_active" className="text-gray-300">Aktif</label>
            </div>

            <div className="flex gap-3">
              <Button type="submit" className="bg-[#FDB714] hover:bg-[#e5a510] text-black">
                {editingId ? 'Güncelle' : 'Ekle'}
              </Button>
              <Button type="button" onClick={resetForm} className="bg-gray-700 hover:bg-gray-600 text-white">
                İptal
              </Button>
            </div>
          </form>
        </Card>
      )}

      {loading ? (
        <p className="text-white text-center">Yükleniyor...</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {smartRatings.map((rating) => (
            <Card key={rating._id} className="bg-gray-900 border-gray-800 p-6">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 border border-gray-700 rounded-lg overflow-hidden flex-shrink-0 bg-gray-800">
                  {rating.icon ? (
                    <img src={rating.icon} alt={rating.label} className="w-full h-full object-contain" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-600">
                      <Upload size={24} />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-bold mb-2">{rating.label}</h3>
                  <p className="text-gray-500 text-sm mb-2">Sıra: {rating.order}</p>
                  <span className={`inline-block px-2 py-1 rounded text-xs ${
                    rating.is_active ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'
                  }`}>
                    {rating.is_active ? 'Aktif' : 'Pasif'}
                  </span>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button
                  onClick={() => handleEdit(rating)}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Edit size={16} className="mr-1" />
                  Düzenle
                </Button>
                <Button
                  onClick={() => handleDelete(rating._id)}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  <Trash2 size={16} className="mr-1" />
                  Sil
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminSmartRatings;
